import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

import { Icon } from '@/components/ui/Icon';
import { resolveButtonToneAppearanceClassName } from './buttonAppearance';
import { resolveGroupedCornerClassName, useButtonGroupContext } from './ButtonGroup';
import { cn } from './cn';

// IconButton — square hit target around a single icon.
//
// Distinct from Button (which carries a text label) — IconButton is for
// tight UI chrome where the only affordance is an icon plus a tooltip /
// aria-label. Sizes are independent from Button: x-small=16, small=24,
// medium=32 (default), large=36 — large matches Button.small height so
// IconButton can sit alongside Button inside a ButtonGroup.
//
// Shares the tone × appearance vocabulary with Button. See
// ./buttonAppearance.js for the supported matrix.
//
// Use Button for labelled actions: <Button icon="globe">Visit site</Button>.
// Use IconButton for icon-only hits: <IconButton icon="close" aria-label="Close" />.

const ICON_BUTTON_BASE_CLASSNAME = 'tw-relative tw-inline-flex tw-shrink-0 tw-select-none tw-items-center tw-justify-center tw-shadow-none tw-ring-offset-background tw-transition-colors focus:tw-outline-none focus-visible:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0 disabled:tw-pointer-events-none disabled:tw-opacity-50';

// Canonical control-height scale shared with Button / Select / Input /
// TextArea: small=32, medium=40, large=44. IconButton keeps an extra
// `tiny` size (16px) reserved for sub-control affordances — the clear /
// reveal toggles inside an Input, the close X inside an Alert, etc.
// These are not "buttons in a row" so they sit outside the canonical
// scale; using a distinct name makes that intent explicit at callsites.
//
// Rounded corners are aligned with Button (`tw-rounded-lg` for the three
// canonical sizes) so a Button + IconButton placed side-by-side at the
// same `size` read as the same family. `tiny` keeps `tw-rounded-sm`
// because 8px of corner on a 16px square would dominate the glyph.
const iconButtonSizeVariants = cva(ICON_BUTTON_BASE_CLASSNAME, {
    variants: {
        size: {
            tiny: 'tw-h-4 tw-w-4 tw-rounded-sm',
            small: 'tw-h-8 tw-w-8 tw-rounded-lg',
            medium: 'tw-h-10 tw-w-10 tw-rounded-lg',
            large: 'tw-h-11 tw-w-11 tw-rounded-lg'
        }
    },
    defaultVariants: {
        size: 'medium'
    }
});

// Every IconButton renders its glyph at 14px regardless of hit-target size.
// The button shell scales (16/24/32/36px) but the icon stays small enough
// to leave breathing room — at large (36px) that's 39% icon-to-button,
// the airier end of the standard 40-50% range.
const renderIconNode = (icon) => {
    if (typeof icon === 'string') {
        return <Icon icon={icon} size="small" tone="inherit" />;
    }

    return icon;
};

const isExternalHref = href => /^https?:\/\//u.test(href ?? '');

const IconButton = React.forwardRef(
    ({
        appearance = 'ghost',
        asChild = false,
        'aria-label': ariaLabel,
        className: _className,
        style: _style,
        disabled,
        href,
        icon,
        loading,
        rel,
        size = 'medium',
        target,
        tone = 'neutral',
        ...props
    }, ref) => {
        const isAnchor = !asChild && Boolean(href);
        const Comp = asChild ? Slot : (isAnchor ? 'a' : 'button');
        const groupContext = useButtonGroupContext();
        const groupedCornerClassName = resolveGroupedCornerClassName(groupContext);
        const toneAppearanceClassName = resolveButtonToneAppearanceClassName(tone, appearance);

        const content = loading
            ? <span className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-current tw-border-t-transparent" />
            : renderIconNode(icon);

        const anchorProps = isAnchor
            ? {
                href,
                rel: rel ?? (isExternalHref(href) ? 'noreferrer noopener' : undefined),
                target: target ?? (isExternalHref(href) ? '_blank' : undefined)
            }
            : null;

        return (
            <Comp
                ref={ref}
                aria-label={ariaLabel}
                className={cn(
                    iconButtonSizeVariants({ size }),
                    toneAppearanceClassName,
                    groupedCornerClassName
                )}
                disabled={isAnchor ? undefined : (loading || disabled)}
                type={asChild || isAnchor ? undefined : 'button'}
                {...anchorProps}
                {...props}
            >
                {content}
            </Comp>
        );
    }
);
IconButton.displayName = 'IconButton';

export { IconButton };
